import { motion } from "framer-motion";
import { format, addDays } from "date-fns";
import { ArrowRight, Flame, CheckCircle2, Plane } from "lucide-react";
import { openInquiry } from "@/lib/scroll";
import CAMPAIGN from "@/data/campaign";

const EASE = [0.76, 0, 0.24, 1];

const nextWeekly = (count, weekday) => {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay() + weekday) % 7 || 7));
  while (days.length < count) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return days;
};

const Batches = () => {
  const b = CAMPAIGN.batches;
  const weekly = nextWeekly(4, b.weeklyWeekday ?? 0);
  const flights = b.flights;

  return (
    <section id="batches" className="py-24 md:py-36 border-t border-border" data-testid="batches-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 mb-14 md:mb-20 items-end">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="uppercase text-xs tracking-[0.25em] font-semibold text-primary mb-6"
              data-testid="batches-overline"
            >
              {b.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
              className="font-heading font-light tracking-tight text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.02]"
              data-testid="batches-heading"
            >
              {b.headingPlain} <em className="text-primary">{b.headingAccent}</em>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="md:col-span-5 text-base md:text-lg font-light text-foreground/75 leading-relaxed"
            data-testid="batches-intro"
          >
            {b.intro}
          </motion.p>
        </div>

        <div>
          {weekly.map((start, i) => {
            const end = addDays(start, 6);
            const hot = i === 0;
            const warm = i === 1;
            return (
              <motion.div
                key={start.toISOString()}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.06 }}
                className="group grid sm:grid-cols-12 gap-3 sm:gap-6 items-center py-6 md:py-8 border-t border-border hover:bg-secondary/40 transition-colors duration-500 md:px-6"
                data-testid={`batch-row-${i}`}
              >
                <div className="sm:col-span-5">
                  <p className="font-heading text-2xl md:text-3xl font-medium text-foreground">
                    {format(start, "dd MMM")} – {format(end, "dd MMM yyyy")}
                  </p>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                    {b.weeklyLabel}
                  </p>
                </div>
                <div className="sm:col-span-3">
                  {hot || warm ? (
                    <span className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 ${hot ? "bg-primary text-primary-foreground" : "border border-primary/40 text-primary"}`} data-testid={`batch-status-${i}`}>
                      <Flame className="w-3.5 h-3.5" /> {hot ? "Filling fast" : "Few seats left"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 border border-border text-muted-foreground" data-testid={`batch-status-${i}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Open
                    </span>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <p className="font-heading text-xl md:text-2xl text-foreground">{b.landPrice}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{b.landNote}</p>
                </div>
                <div className="sm:col-span-2 sm:text-right">
                  <button
                    onClick={openInquiry}
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-primary border-b border-primary/40 pb-1 hover:gap-4 transition-all duration-300"
                    data-testid={`batch-cta-${i}`}
                  >
                    Reserve this batch <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
          <div className="border-t border-border" />
        </div>

        {flights && flights.list.length > 0 && (
          <div className="mt-20 md:mt-28">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="uppercase text-xs tracking-[0.25em] font-semibold text-primary mb-4"
              data-testid="flight-batches-overline"
            >
              {flights.overline}
            </motion.p>
            <motion.h3
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="font-heading font-light tracking-tight text-foreground text-3xl md:text-4xl mb-10"
              data-testid="flight-batches-heading"
            >
              {flights.headingPlain} <em className="text-primary">{flights.headingAccent}</em>
            </motion.h3>

            <div>
              {flights.list.map((f, i) => {
                const start = new Date(f.start);
                return (
                  <motion.div
                    key={f.start}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.7, ease: EASE, delay: i * 0.06 }}
                    className="group grid sm:grid-cols-12 gap-3 sm:gap-6 items-center py-6 md:py-8 border-t border-border hover:bg-secondary/40 transition-colors duration-500 md:px-6"
                    data-testid={`flight-batch-row-${i}`}
                  >
                    <div className="sm:col-span-5">
                      <p className="font-heading text-2xl md:text-3xl font-medium text-foreground">
                        {format(start, "dd MMM")} – {format(addDays(start, 6), "dd MMM yyyy")}
                      </p>
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                        {flights.note}
                      </p>
                    </div>
                    <div className="sm:col-span-3">
                      <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 bg-[#1A362B] text-[#F5F2EB]" data-testid={`flight-batch-tag-${i}`}>
                        <Plane className="w-3.5 h-3.5" /> {f.tag}
                      </span>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="font-heading text-xl md:text-2xl text-foreground">{f.price}</p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{b.landNote}</p>
                    </div>
                    <div className="sm:col-span-2 sm:text-right">
                      <button
                        onClick={openInquiry}
                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-primary border-b border-primary/40 pb-1 hover:gap-4 transition-all duration-300"
                        data-testid={`flight-batch-cta-${i}`}
                      >
                        Reserve this batch <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
              <div className="border-t border-border" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Batches;
